// src/Dashboard.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, setStoreIdHeader, setUserHeader } from "./api";

import RegisterUsers from "./RegisterUsers";
import UsersList from "./UsersList";
import BusinessList from "./BusinessList";
import InventoryList from "./InventoryList";
import SalesWindow from "./SalesWindow";
import Reports from "./Reports";
import SalesSummaryReport from "./SalesSummaryReport";
import ReplenishReport from "./ReplenishReport";
import POSUI from "./POSUI";
import SaleItemsReport from "./SaleItemsReport";

import ReplenishmentReport from "./ReplenishmentReport";
import StockReport from "./StockReport";

// ✅ Add these two
import RegisterStore from "./RegisterStore";
import UsersManager from "./UsersManager";

function safeParseJSON(v) {
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
}

export default function Dashboard() {
  const navigate = useNavigate();

  const [page, setPage] = useState("home");
  const [navOpen, setNavOpen] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [showTiles, setShowTiles] = useState(true);

  const [storeId, setStoreId] = useState(() =>
    Number(localStorage.getItem("store_id") || 0)
  );
  const [posType, setPosType] = useState(() =>
    (localStorage.getItem("pos_type") || "retail").toLowerCase()
  );

  const fileBtnRef = useRef(null);
  const fileMenuRef = useRef(null);
  const reportsBtnRef = useRef(null);
  const reportsMenuRef = useRef(null);

  // used to restore store id after register-store
  const prevStoreIdRef = useRef(0);

  /* -------------------- Force LIGHT theme -------------------- */
  useEffect(() => {
    localStorage.setItem("theme", "light");
    document.documentElement.classList.remove("dark");
  }, []);

  /* -------------------- Session (reactive) -------------------- */
  const [rawSession, setRawSession] = useState(() =>
    safeParseJSON(localStorage.getItem("user") || "null")
  );

  // keep session in sync when localStorage changes (other tabs, or your own writes)
  useEffect(() => {
    const sync = () => setRawSession(safeParseJSON(localStorage.getItem("user") || "null"));

    // initial sync (covers cases where Dashboard mounts before login write completes)
    sync();

    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  // Supports both shapes:
  // 1) { success:true, store_id:.., user:{ role, permissions, username } }
  // 2) { role, permissions, username }
  const sessionUser = rawSession?.user || rawSession || null;

  const role = String(sessionUser?.role || "").toLowerCase();

  const permissions = useMemo(() => {
    const p = sessionUser?.permissions;
    return Array.isArray(p) ? p : [];
  }, [sessionUser]);

  const usernameFromSession =
    sessionUser?.username || localStorage.getItem("username") || "";

  // ✅ Permission check:
  // - super_user role => allow all
  // - permissions ["*"] => allow all
  // - else exact permission
  const can = (perm) => {
    if (role === "super_user") return true;
    if (permissions.includes("*")) return true;
    return permissions.includes(perm);
  };

  // Business Registration: super_user OR admin ONLY
  const canBusiness = () =>
    role === "super_user" || permissions.includes("*") || role === "admin";

  // ✅ Register Store: super_user ONLY (or star)
  const canRegisterStore = () =>
    role === "super_user" || permissions.includes("*");

  /* -------------------- boot / headers -------------------- */
  useEffect(() => {
    const userStr = localStorage.getItem("user");
    const sid = Number(localStorage.getItem("store_id") || 0);

    if (!userStr || !sid) {
      navigate("/login", { replace: true });
      return;
    }

    const u = safeParseJSON(userStr);

    setStoreId(sid);
    setStoreIdHeader(sid);
    localStorage.setItem("store_id", String(sid));

    const username = u?.user?.username || u?.username || "";
    setUserHeader(username);
    if (username) localStorage.setItem("username", username);

    const pt = (localStorage.getItem("pos_type") || "retail").toLowerCase();
    setPosType(pt);
  }, [navigate]);

  // IMPORTANT: only apply store header when we are NOT on register-store page
  useEffect(() => {
    if (page === "register-store") return;
    setStoreIdHeader(storeId);
    if (storeId > 0) localStorage.setItem("store_id", String(storeId));
  }, [storeId, page]);

  const openDefaultForType = (t) => {
    const type = (t || "").toLowerCase();
    if (type === "bar") {
      if (can("pos.sell")) {
        setPage("pos");
        setShowTiles(false);
      } else {
        setPage("home");
        setShowTiles(true);
      }
    } else {
      if (can("pos.sell")) {
        setPage("sale");
        setShowTiles(false);
      } else {
        setPage("home");
        setShowTiles(true);
      }
    }
  };

  useEffect(() => {
    openDefaultForType(posType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -------------------- click outside dropdown -------------------- */
  useEffect(() => {
    const onClick = (e) => {
      if (
        fileOpen &&
        fileMenuRef.current &&
        !fileMenuRef.current.contains(e.target) &&
        fileBtnRef.current &&
        !fileBtnRef.current.contains(e.target)
      ) {
        setFileOpen(false);
      }

      if (
        reportsOpen &&
        reportsMenuRef.current &&
        !reportsMenuRef.current.contains(e.target) &&
        reportsBtnRef.current &&
        !reportsBtnRef.current.contains(e.target)
      ) {
        setReportsOpen(false);
      }
    };

    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [fileOpen, reportsOpen]);

  const go = (p) => {
    setPage(p);
    setNavOpen(false);
    setFileOpen(false);
    setReportsOpen(false);
  };

  const handleTileSelect = (cb) => {
    cb();
    setShowTiles(false);
  };

  const handleShowMenu = () => {
    // restore store context if we came from register-store
    if (page === "register-store" && prevStoreIdRef.current > 0) {
      setStoreId(prevStoreIdRef.current);
      setStoreIdHeader(prevStoreIdRef.current);
      localStorage.setItem("store_id", String(prevStoreIdRef.current));
    }

    setShowTiles(true);
    setPage("home");
    window.scrollTo?.(0, 0);
  };

  const focusFirstMenuItem = (menuRef) => {
    menuRef.current?.querySelector('[role="menuitem"]')?.focus();
  };

  const onDropdownButtonKeyDown = (e, which) => {
    if (e.key === "Escape") {
      if (which === "file") setFileOpen(false);
      if (which === "reports") setReportsOpen(false);
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (which === "file") focusFirstMenuItem(fileMenuRef);
      if (which === "reports") focusFirstMenuItem(reportsMenuRef);
    }
  };

  const onMenuItemKeyDown = (e, which) => {
    const menuRef = which === "file" ? fileMenuRef : reportsMenuRef;
    const btnRef = which === "file" ? fileBtnRef : reportsBtnRef;

    const items = Array.from(
      menuRef.current?.querySelectorAll('[role="menuitem"]') || []
    );
    if (!items.length) return;

    const idx = items.indexOf(e.currentTarget);

    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(idx + 1) % items.length]?.focus();
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    }
    if (e.key === "Escape") {
      if (which === "file") setFileOpen(false);
      if (which === "reports") setReportsOpen(false);
      btnRef.current?.focus();
    }
  };

  const type = (posType || "retail").toLowerCase();
  const isBar = type === "bar";
  const isRetail = type === "retail";

  const doLogout = () => {
    const apiBase = localStorage.getItem("api_base");
    localStorage.clear();
    if (apiBase) localStorage.setItem("api_base", apiBase);

    localStorage.setItem("theme", "light");
    document.documentElement.classList.remove("dark");

    setStoreIdHeader(0);
    setUserHeader("");
    setPosType("retail");
    setStoreId(0);

    navigate("/login", { replace: true });
  };

  // ✅ open register store WITHOUT store scope header
  const openRegisterStore = () => {
    if (!canRegisterStore()) return;

    // remember current store to restore later
    prevStoreIdRef.current = Number(localStorage.getItem("store_id") || storeId || 0);

    // clear store scope for creating a new store
    setStoreId(0);
    setStoreIdHeader(0);

    // keep user header
    const u = localStorage.getItem("username") || usernameFromSession || "";
    setUserHeader(u);

    go("register-store");
  };

  /* -------------------- Actions (Tiles) with RBAC -------------------- */
  const actions = useMemo(() => {
    const a = [];

    // POS / Selling
    if (can("pos.sell")) {
      if (isRetail) {
        a.push({
          key: "sale",
          label: "New Sale",
          icon: "🛒",
          onClick: () => go("sale"),
        });
      }
      if (isBar) {
        a.push({
          key: "pos",
          label: "Point of Sale (Bar)",
          icon: "🍹",
          onClick: () => go("pos"),
        });
      }
    }

    // Inventory
    if (can("inventory.view")) {
      a.push({
        key: "inventory",
        label: "Inventory",
        icon: "📦",
        onClick: () => go("inventory"),
      });
    }

    // Reports
    if (can("reports.view")) {
      a.push({
        key: "reports",
        label: "Sales Report",
        icon: "📈",
        onClick: () => go("reports"),
      });
      a.push({
        key: "sales-summary",
        label: "Sales Summary",
        icon: "👤📊",
        onClick: () => go("sales-summary"),
      });
      a.push({
        key: "sale-items-report",
        label: "Sales Items Report",
        icon: "🧾",
        onClick: () => go("sale-items-report"),
      });
      a.push({
        key: "replenishment-report",
        label: "Replenishment Report",
        icon: "➕",
        onClick: () => go("replenishment-report"),
      });
      a.push({
        key: "stock-report",
        label: "Stock Report",
        icon: "📦🧾",
        onClick: () => go("stock-report"),
      });
      a.push({
        key: "replenish-report",
        label: "Legacy Replenish Page",
        icon: "🧰",
        onClick: () => go("replenish-report"),
      });
    }

    // ✅ Users (Admin)
    if (can("users.manage")) {
      a.push({
        key: "users-manager",
        label: "User Management",
        icon: "👥",
        onClick: () => go("users-manager"),
      });
    }

    // ✅ Business Registration (super/admin only)
    if (canBusiness()) {
      a.push({
        key: "business",
        label: "Business Registration",
        icon: "🏢",
        onClick: () => go("business"),
      });
    }

    // ✅ Register Store (super_user only)
    if (canRegisterStore()) {
      a.push({
        key: "register-store",
        label: "Register Store",
        icon: "🏪",
        onClick: () => openRegisterStore(),
      });
    }

    // Always show logout
    a.push({ key: "logout", label: "Logout", icon: "🚪", onClick: doLogout });

    return a;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRetail, isBar, role, permissions.join("|")]);

  const Forbidden = ({ need }) => (
    <div className="notice">
      <b>Forbidden.</b> You don’t have access. Required: <code>{need}</code>
    </div>
  );

  const username = usernameFromSession || "";

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brandWrap">
          <div className="brandMark" aria-hidden="true">
            K
          </div>
          <div className="brandText">
            <div className="brandTitle">KevSoft POS</div>
            <div className="brandSub">
              Store #{storeId || "—"} · {type.toUpperCase()}
            </div>
          </div>
        </div>

        <button
          className="iconBtn hamburger"
          aria-label="Toggle navigation"
          aria-expanded={navOpen}
          onClick={() => setNavOpen((v) => !v)}
        >
          ☰
        </button>

        <nav className={`nav ${navOpen ? "open" : ""}`}>
          <div className="dropdown">
            <button
              ref={fileBtnRef}
              className="navBtn"
              aria-haspopup="true"
              aria-expanded={fileOpen}
              onClick={() => {
                setFileOpen((v) => !v);
                setReportsOpen(false);
              }}
              onKeyDown={(e) => onDropdownButtonKeyDown(e, "file")}
            >
              File <span className="caret">▾</span>
            </button>

            <div
              ref={fileMenuRef}
              className={`menu ${fileOpen ? "show" : ""}`}
              role="menu"
              aria-label="File"
            >
              {can("users.manage") && (
                <button
                  role="menuitem"
                  onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                  onClick={() => handleTileSelect(() => go("users-manager"))}
                >
                  👥 User Management
                </button>
              )}

              {/* ✅ Business Registration super/admin-only */}
              {canBusiness() && (
                <button
                  role="menuitem"
                  onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                  onClick={() => handleTileSelect(() => go("business"))}
                >
                  🏢 Business Registration
                </button>
              )}

              {/* ✅ Register Store super_user-only */}
              {canRegisterStore() && (
                <button
                  role="menuitem"
                  onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                  onClick={() => handleTileSelect(() => openRegisterStore())}
                >
                  🏪 Register Store
                </button>
              )}

              <div className="menuSep" />

              <button
                role="menuitem"
                onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                onClick={doLogout}
                className="danger"
              >
                🚪 Logout
              </button>
            </div>
          </div>

          <div className="dropdown">
            <button
              ref={reportsBtnRef}
              className="navBtn"
              aria-haspopup="true"
              aria-expanded={reportsOpen}
              onClick={() => {
                setReportsOpen((v) => !v);
                setFileOpen(false);
              }}
              onKeyDown={(e) => onDropdownButtonKeyDown(e, "reports")}
            >
              Reports <span className="caret">▾</span>
            </button>

            <div
              ref={reportsMenuRef}
              className={`menu ${reportsOpen ? "show" : ""}`}
              role="menu"
              aria-label="Reports"
            >
              {can("reports.view") ? (
                <>
                  <button
                    role="menuitem"
                    onKeyDown={(e) => onMenuItemKeyDown(e, "reports")}
                    onClick={() => handleTileSelect(() => go("reports"))}
                  >
                    📈 Sales Report
                  </button>

                  <button
                    role="menuitem"
                    onKeyDown={(e) => onMenuItemKeyDown(e, "reports")}
                    onClick={() => handleTileSelect(() => go("sales-summary"))}
                  >
                    👤📊 Sales Summary
                  </button>

                  <button
                    role="menuitem"
                    onKeyDown={(e) => onMenuItemKeyDown(e, "reports")}
                    onClick={() => handleTileSelect(() => go("sale-items-report"))}
                  >
                    🧾 Sales Items
                  </button>

                  <div className="menuSep" />

                  <button
                    role="menuitem"
                    onKeyDown={(e) => onMenuItemKeyDown(e, "reports")}
                    onClick={() => handleTileSelect(() => go("replenishment-report"))}
                  >
                    ➕ Replenishment
                  </button>

                  <button
                    role="menuitem"
                    onKeyDown={(e) => onMenuItemKeyDown(e, "reports")}
                    onClick={() => handleTileSelect(() => go("stock-report"))}
                  >
                    📦 Stock Report
                  </button>
                </>
              ) : (
                <div style={{ padding: 10, opacity: 0.85 }}>
                  Missing <code>reports.view</code>
                </div>
              )}
            </div>
          </div>

          <div className="quickActions">
            {can("pos.sell") && isRetail && (
              <button className="pillBtn" onClick={() => handleTileSelect(() => go("sale"))}>
                🛒 New Sale
              </button>
            )}
            {can("pos.sell") && isBar && (
              <button className="pillBtn" onClick={() => handleTileSelect(() => go("pos"))}>
                🍹 POS (Bar)
              </button>
            )}
            {can("inventory.view") && (
              <button className="pillBtn" onClick={() => handleTileSelect(() => go("inventory"))}>
                📦 Inventory
              </button>
            )}
            {can("reports.view") && (
              <>
                <button
                  className="pillBtn"
                  onClick={() => handleTileSelect(() => go("replenishment-report"))}
                >
                  ➕ Replenish
                </button>
                <button className="pillBtn" onClick={() => handleTileSelect(() => go("stock-report"))}>
                  📦🧾 Stock
                </button>
              </>
            )}
          </div>
        </nav>

        <div className="rightMeta">
          <div className="userChip" title={username ? `Logged in as ${username}` : "Logged in"}>
            <span className="dot" />
            <span className="userText">{username || "User"}</span>
          </div>
        </div>
      </header>

      <main className="content">
        {!showTiles && (
          <div className="toolbar">
            <button className="primaryBtn" onClick={handleShowMenu}>
              ◀ Back to Menu
            </button>
            <div className="crumbs">
              <span className="crumbLabel">Page:</span>
              <span className="crumbValue">{page}</span>
            </div>
          </div>
        )}

        {showTiles && (
          <section className="tileGrid" aria-label="Quick actions">
            <div className="tileHeader">
              <div className="tileTitle">Dashboard</div>
              <div className="tileSubtitle">Choose an action to continue.</div>
            </div>

            <div className="tiles">
              {actions.map((a) => (
                <button key={a.key} className="tile" onClick={() => handleTileSelect(a.onClick)}>
                  <div className="tileIcon" aria-hidden="true">
                    {a.icon}
                  </div>
                  <div className="tileText">
                    <div className="tileLabel">{a.label}</div>
                    <div className="tileHint">Open</div>
                  </div>
                  <div className="tileArrow" aria-hidden="true">
                    ›
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {!showTiles && (
          <section className="pageCard">
            {page === "home" && <h1 className="pageTitle">Welcome to your POS Dashboard</h1>}

            {/* ✅ WORKING user management */}
            {page === "users-manager" &&
              (can("users.manage") ? <UsersManager onBack={handleShowMenu} /> : <Forbidden need="users.manage" />)}

            {/* kept for backward compatibility */}
            {page === "users-list" &&
              (can("users.manage") ? <UsersList onBack={handleShowMenu} api={api} /> : <Forbidden need="users.manage" />)}

            {page === "register-users" &&
              (can("users.manage") ? (
                <RegisterUsers onBack={handleShowMenu} api={api} />
              ) : (
                <Forbidden need="users.manage" />
              ))}

            {/* ✅ Business Registration super/admin-only */}
            {page === "business" &&
              (canBusiness() ? <BusinessList onBack={handleShowMenu} api={api} /> : <Forbidden need="admin/super_user" />)}

            {/* ✅ Register Store super_user-only */}
            {page === "register-store" &&
              (canRegisterStore() ? (
                <RegisterStore onSuccess={() => handleShowMenu()} />
              ) : (
                <Forbidden need="super_user" />
              ))}

            {page === "inventory" &&
              (can("inventory.view") ? <InventoryList onBack={handleShowMenu} api={api} /> : <Forbidden need="inventory.view" />)}

            {page === "sale" &&
              isRetail &&
              (can("pos.sell") ? <SalesWindow onBack={handleShowMenu} api={api} /> : <Forbidden need="pos.sell" />)}

            {page === "reports" &&
              (can("reports.view") ? <Reports onBack={handleShowMenu} api={api} /> : <Forbidden need="reports.view" />)}

            {page === "sales-summary" &&
              (can("reports.view") ? (
                <SalesSummaryReport onBack={handleShowMenu} api={api} />
              ) : (
                <Forbidden need="reports.view" />
              ))}

            {page === "sale-items-report" &&
              (can("reports.view") ? <SaleItemsReport onBack={handleShowMenu} /> : <Forbidden need="reports.view" />)}

            {page === "replenishment-report" &&
              (can("reports.view") ? <ReplenishmentReport onBack={handleShowMenu} /> : <Forbidden need="reports.view" />)}

            {page === "stock-report" &&
              (can("reports.view") ? <StockReport onBack={handleShowMenu} /> : <Forbidden need="reports.view" />)}

            {page === "replenish-report" &&
              (can("reports.view") ? <ReplenishReport onBack={handleShowMenu} api={api} /> : <Forbidden need="reports.view" />)}

            {page === "pos" &&
              isBar &&
              (can("pos.sell") ? <POSUI onBack={handleShowMenu} api={api} /> : <Forbidden need="pos.sell" />)}

            {page === "sale" && isBar && (
              <div className="notice">
                This store is set to <b>bar</b>. Retail POS is hidden.
              </div>
            )}
            {page === "pos" && isRetail && (
              <div className="notice">
                This store is set to <b>retail</b>. Bar POS is hidden.
              </div>
            )}
          </section>
        )}
      </main>

      {/* ✅ YOUR ORIGINAL CSS (unchanged) */}
      <style>{`
        :root{
          --bg:#f6f7fb;
          --surface:#ffffff;
          --stroke:rgba(15,23,42,.12);
          --text:#0f172a;
          --muted:#475569;
          --brand:#2563eb;
          --brand2:#1d4ed8;
          --good:#16a34a;
          --danger:#dc2626;
          --shadow: 0 18px 50px rgba(15,23,42,.10);
          --shadow2: 0 10px 28px rgba(15,23,42,.10);
          --radius:16px;
          --ring: 0 0 0 3px rgba(37,99,235,.22);
        }

        *{box-sizing:border-box}
        html, body { background: var(--bg) !important; color: var(--text) !important; }
        body{
          margin:0;
          background:
            radial-gradient(1000px 400px at 20% -10%, rgba(37,99,235,.10), transparent 55%),
            radial-gradient(900px 360px at 80% 0%, rgba(22,163,74,.06), transparent 60%),
            var(--bg) !important;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
        }

        .app-shell{ min-height:100vh; display:flex; flex-direction:column; }

        .topbar{
          position:sticky; top:0; z-index:50;
          background: rgba(255,255,255,.78);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid var(--stroke);
          display:flex; align-items:center; gap:14px;
          padding: 10px 14px;
        }

        .brandWrap{ display:flex; align-items:center; gap:12px; min-width:220px; }
        .brandMark{
          width:40px; height:40px; border-radius:14px;
          background: linear-gradient(135deg, var(--brand), #60a5fa);
          color:white; font-weight:900; display:grid; place-items:center;
          box-shadow: 0 10px 25px rgba(37,99,235,.25);
        }
        .brandTitle{ font-weight:900; letter-spacing:.2px; }
        .brandSub{ font-size:12px; color:var(--muted); margin-top:2px; }

        .hamburger{ display:none; margin-left:auto; }

        .iconBtn{
          border:1px solid var(--stroke);
          background: var(--surface);
          border-radius: 12px;
          padding: 10px 12px;
          cursor:pointer;
        }
        .iconBtn:focus{ outline:none; box-shadow: var(--ring); }

        .nav{ display:flex; align-items:center; gap:10px; flex:1; }
        .nav.open{ display:flex; }

        .navBtn{
          border:1px solid var(--stroke);
          background: var(--surface);
          border-radius: 12px;
          padding: 10px 12px;
          cursor:pointer;
          display:flex; align-items:center; gap:8px;
        }
        .navBtn:focus{ outline:none; box-shadow: var(--ring); }
        .caret{ opacity:.7; }

        .dropdown{ position:relative; }
        .menu{
          position:absolute; top:44px; left:0;
          min-width:240px;
          background: var(--surface);
          border:1px solid var(--stroke);
          border-radius: 14px;
          box-shadow: var(--shadow2);
          padding: 8px;
          display:none;
        }
        .menu.show{ display:block; }
        .menu button{
          width:100%;
          text-align:left;
          border:1px solid transparent;
          background:transparent;
          padding:10px 10px;
          border-radius:12px;
          cursor:pointer;
        }
        .menu button:hover{ background:#f3f4f6; }
        .menu button:focus{ outline:none; box-shadow: var(--ring); }
        .menuSep{ height:1px; background: var(--stroke); margin: 8px 4px; }
        .danger{ color: var(--danger); }

        .quickActions{ display:flex; gap:8px; margin-left:10px; flex-wrap:wrap; }
        .pillBtn{
          border:1px solid var(--stroke);
          background: var(--surface);
          border-radius: 999px;
          padding: 9px 12px;
          cursor:pointer;
          font-weight:700;
        }
        .pillBtn:hover{ background:#f8fafc; }

        .rightMeta{ margin-left:auto; display:flex; align-items:center; gap:10px; }
        .userChip{
          display:flex; align-items:center; gap:10px;
          border:1px solid var(--stroke);
          background: var(--surface);
          border-radius: 999px;
          padding: 8px 12px;
        }
        .dot{ width:10px; height:10px; border-radius:999px; background: var(--good); box-shadow: 0 0 0 3px rgba(22,163,74,.15); }
        .userText{ font-weight:800; font-size:13px; }

        .content{ flex:1; padding: 14px; }
        .toolbar{
          display:flex; align-items:center; justify-content:space-between;
          margin: 6px 0 12px;
          padding: 10px 12px;
          border: 1px solid var(--stroke);
          border-radius: var(--radius);
          background: rgba(255,255,255,.7);
        }
        .primaryBtn{
          border:1px solid var(--brand);
          background: var(--brand);
          color:white;
          border-radius: 12px;
          padding: 10px 14px;
          font-weight:900;
          cursor:pointer;
        }
        .primaryBtn:hover{ background: var(--brand2); }
        .crumbs{ color:var(--muted); font-size:13px; display:flex; gap:8px; align-items:center; }
        .crumbValue{ font-weight:800; color:var(--text); }

        .tileGrid{
          border:1px solid var(--stroke);
          border-radius: var(--radius);
          background: rgba(255,255,255,.72);
          padding: 14px;
          box-shadow: var(--shadow);
        }
        .tileHeader{ margin-bottom: 12px; }
        .tileTitle{ font-size: 18px; font-weight: 900; }
        .tileSubtitle{ color: var(--muted); font-size: 13px; margin-top:4px; }

        .tiles{
          display:grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 12px;
        }
        .tile{
          display:flex;
          gap: 12px;
          align-items:center;
          border:1px solid var(--stroke);
          border-radius: 18px;
          padding: 14px;
          background: var(--surface);
          cursor:pointer;
          box-shadow: 0 10px 30px rgba(15,23,42,.06);
        }
        .tile:hover{
          border-color: rgba(37,99,235,.35);
          box-shadow: 0 16px 44px rgba(37,99,235,.12);
        }
        .tileIcon{ font-size: 22px; width:40px; height:40px; display:grid; place-items:center; border-radius: 14px; background:#f1f5f9; }
        .tileLabel{ font-weight: 900; }
        .tileHint{ font-size: 12px; color: var(--muted); margin-top:2px; }
        .tileArrow{ margin-left:auto; font-size: 22px; opacity: .55; }

        .pageCard{
          border:1px solid var(--stroke);
          border-radius: var(--radius);
          background: var(--surface);
          padding: 14px;
          box-shadow: var(--shadow);
        }
        .pageTitle{ margin:0; }
        .notice{
          margin-top: 10px;
          border:1px dashed rgba(15,23,42,.25);
          background: #f8fafc;
          padding: 12px;
          border-radius: 14px;
          color: var(--text);
        }

        @media (max-width: 900px){
          .hamburger{ display:inline-flex; }
          .nav{ display:none; flex-direction:column; align-items:flex-start; width:100%; padding-top: 10px; }
          .nav.open{ display:flex; }
          .rightMeta{ display:none; }
          .brandWrap{ min-width: unset; }
        }
      `}</style>
    </div>
  );
}